import {
  Home,
  Truck,
  ArrowUpToLine,
  Package,
  Warehouse,
  Briefcase,
  Phone,
} from 'lucide-react';
import { KineticLines, FadeUp, Eyebrow } from '../components/motion';
import { CTASection } from '../components/CTASection';
import { EditorialMarquee } from '../components/EditorialMarquee';
import { SERVICES, PROCESS_STEPS, IMAGES, PHONE_TEL } from '../data/content';

const ICONS = { Home, Truck, ArrowUpToLine, Package, Warehouse, Briefcase };

const PageHero = () => (
  <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.transport} alt="Nakliyat aracı" className="w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060B19]/70 via-[#060B19]/85 to-[#060B19]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp delay={0.1}>
        <Eyebrow>Hizmetlerimiz</Eyebrow>
      </FadeUp>
      <KineticLines
        className="mt-6 font-display font-medium uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl"
        lines={[
          <span key="1" className="block">Kapıdan kapıya,</span>,
          <span key="2" className="block text-[#FF5C00]">uçtan uca hizmet</span>,
        ]}
      />
      <FadeUp delay={0.6} className="mt-7 max-w-xl">
        <p className="text-[#A0ABC0] text-base md:text-lg leading-relaxed">
          Keşiften kuruluma kadar taşınma sürecinin tamamını tek elden yönetiyoruz.
        </p>
      </FadeUp>
    </div>
  </section>
);

const ServiceGrid = () => (
  <section data-testid="services-detail-grid" className="py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
      {SERVICES.map((s, i) => {
        const Icon = ICONS[s.icon];
        return (
          <FadeUp key={s.id} delay={(i % 2) * 0.1}>
            <div
              data-testid={`service-detail-${s.id}`}
              className="card-hover group h-full bg-[#0A1128] border border-[#1E293B] rounded-2xl p-9 md:p-11 flex gap-7"
            >
              <div className="shrink-0 w-14 h-14 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center transition-colors duration-500 group-hover:bg-[#FF5C00]/20">
                <Icon size={26} className="text-[#FF5C00]" />
              </div>
              <div>
                <h3 className="font-display text-2xl tracking-tight mb-3">{s.title}</h3>
                <p className="text-[#A0ABC0] leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </FadeUp>
        );
      })}
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="py-24 md:py-32 bg-[#0A1128] border-y border-[#1E293B]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div>
          <Eyebrow>Nasıl Çalışıyoruz?</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight">
            4 adımda <span className="text-[#FF5C00]">yeni evinizdesiniz</span>
          </h2>
        </div>
        <a
          href={PHONE_TEL}
          data-testid="process-call-button"
          className="inline-flex items-center gap-3 bg-[#FF5C00] hover:bg-[#E65000] text-[#0A1128] font-bold rounded-full px-7 py-4 transition-colors duration-300"
        >
          <Phone size={17} strokeWidth={2.5} />
          Keşif Randevusu Al
        </a>
      </FadeUp>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PROCESS_STEPS.map((p, i) => (
          <FadeUp key={p.no} delay={i * 0.1}>
            <div
              data-testid={`process-step-${p.no}`}
              className="relative h-full border border-[#1E293B] rounded-2xl p-8 bg-[#060B19]"
            >
              <span className="font-display font-semibold text-6xl tracking-tighter text-stroke">
                {p.no}
              </span>
              <h3 className="font-display text-xl tracking-tight mt-6 mb-3">{p.title}</h3>
              <p className="text-[#A0ABC0] text-sm leading-relaxed">{p.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ImageStrip = () => (
  <section data-testid="services-image-strip" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-5">
      {[
        { src: IMAGES.packing, title: 'Ambalajlama', desc: 'Her eşya kategorisine özel paketleme' },
        { src: IMAGES.movers, title: 'Uzman Ekip', desc: 'Deneyimli, sigortalı taşıma personeli' },
      ].map((img, i) => (
        <FadeUp key={i} delay={i * 0.12}>
          <div className="group relative rounded-2xl overflow-hidden border border-[#1E293B] h-[320px] md:h-[420px]">
            <img
              src={img.src}
              alt={img.title}
              loading="lazy"
              className="img-zoom absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060B19]/95 via-[#060B19]/20 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-display text-2xl tracking-tight">{img.title}</h3>
              <p className="text-[#A0ABC0] text-sm mt-2">{img.desc}</p>
            </div>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

export default function HizmetlerPage() {
  return (
    <main data-testid="services-page">
      <PageHero />
      <EditorialMarquee />
      <ServiceGrid />
      <Process />
      <ImageStrip />
      <CTASection />
    </main>
  );
}
